<template>
  <modal-wrapper
    v-model:is-visible="isVisible"
    :busy="isSubmitting"
    :close-on-ok-click="false"
    :content-overflow-y="'auto'"
    :ok-button-disabled="!selectedDocuments.length"
    :ok-button-label="okButtonLabel"
    :size="ModalSize.XL"
    @ok-button-click="handleOk"
    @cancel-button-click="handleCancel"
  >
    <h3 class="picker-modal__title">{{ title }}</h3>
    <p class="picker-modal__selection-count">{{ selectionCountLabel }}</p>
    <workspace-document-simple-search
      ref="searchRef"
      :is-visible="isVisible"
      :mode="mode"
      :workspace-id="workspaceId"
      :workspace-location="workspaceLocation"
      @selection-change="handleSelectionChange"
    />
  </modal-wrapper>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';
  import {
    IWorkspaceDocumentSearchResultItemDto,
    WorkspaceDocumentPickerMode,
  } from 'types';
  import { useI18n } from 'vue-i18n';
  import { ModalSize } from '@/components/ModalWrapper';
  import WorkspaceDocumentSimpleSearch from './WorkspaceDocumentSimpleSearch.vue';

  const props = defineProps<{
    workspaceId: string;
    workspaceLocation: string;
    mode: WorkspaceDocumentPickerMode;
    onConfirm?: (payload: {
      mode: WorkspaceDocumentPickerMode;
      documents: IWorkspaceDocumentSearchResultItemDto[];
    }) => Promise<boolean>;
  }>();

  const { t } = useI18n();
  const isVisible = ref(false);

  const selectedDocuments = ref<IWorkspaceDocumentSearchResultItemDto[]>([]);
  const isSubmitting = ref(false);
  const searchRef = ref<InstanceType<
    typeof WorkspaceDocumentSimpleSearch
  > | null>(null);

  const title = computed(() =>
    props.mode === WorkspaceDocumentPickerMode.EDIT
      ? t('workspaceDocuments.picker.addForEditing')
      : t('workspaceDocuments.picker.addForViewing'),
  );

  const okButtonLabel = computed(() =>
    props.mode === WorkspaceDocumentPickerMode.EDIT
      ? t('workspaceDocuments.picker.addForEditing')
      : t('workspaceDocuments.picker.addForViewing'),
  );

  const selectionCountLabel = computed(() =>
    t('workspaceDocuments.picker.selectionDone', {
      count: selectedDocuments.value.length,
    }),
  );

  function show(): void {
    isVisible.value = true;
  }

  function hide(): void {
    isVisible.value = false;
  }

  defineExpose({ show, hide });

  function handleSelectionChange(
    value: IWorkspaceDocumentSearchResultItemDto[],
  ): void {
    selectedDocuments.value = value;
  }

  async function handleOk(): Promise<void> {
    if (isSubmitting.value) {
      return;
    }

    const payload = {
      mode: props.mode,
      documents: selectedDocuments.value,
    };

    isSubmitting.value = true;
    try {
      const shouldClose = props.onConfirm
        ? await props.onConfirm(payload)
        : true;
      if (!shouldClose) {
        return;
      }

      selectedDocuments.value = [];
      searchRef.value?.resetSelection();
      hide();
    } finally {
      isSubmitting.value = false;
    }
  }

  function handleCancel(): void {
    selectedDocuments.value = [];
    searchRef.value?.resetSelection();
  }
</script>

<style scoped lang="scss">
  .picker-modal__title {
    margin-bottom: 0.35rem;
    font-size: 1.5rem;
    font-weight: 700;
  }

  .picker-modal__selection-count {
    margin: 0 0 1rem;
    color: #5f6b7d;
    font-size: 0.95rem;
    font-weight: 600;
  }
</style>
